<template>
  <div class="bidding" v-loading="stockListLoading" element-loading-text="查询中...">
    <!-- 日期查询区域：H5 日期与概念分两行 -->
    <div class="date-area" :class="{ 'date-area--mobile': isMobile }">
      <template v-if="!isMobile">
        <div class="date-area__segment">
          <span class="date-label">日期:</span>
          <el-date-picker
            :size="isMini"
            v-model="currentDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
            class="search-area-select"
            @change="handleDateChange"
          />
        </div>
        <div class="date-area__segment date-area__segment--after-date">
          <span class="date-label">概念:</span>
          <el-select
            :size="isMini"
            v-model="conceptSource"
            class="search-area-select"
            @change="onConceptSourceChange"
          >
            <el-option
              v-for="item in conceptSourceOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <el-button
          type="primary"
          :size="isMini"
          class="date-area__sync-btn"
          :loading="syncingDayData"
          @click="handleSyncDayData"
        >
          获取数据
        </el-button>
      </template>
      <template v-else>
        <div class="date-area__row">
          <span class="date-label">日期:</span>
          <el-date-picker
            size="mini"
            v-model="currentDate"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
            class="search-area-select"
            @change="handleDateChange"
          />
        </div>
        <div class="date-area__row date-area__row--concept">
          <span class="date-label">概念:</span>
          <el-select
            size="mini"
            v-model="conceptSource"
            class="search-area-select"
            :popper-append-to-body="false"
            @change="onConceptSourceChange"
          >
            <el-option
              v-for="item in conceptSourceOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-button
            type="primary"
            size="mini"
            class="date-area__sync-btn"
            :loading="syncingDayData"
            @click="handleSyncDayData"
          >
            获取数据
          </el-button>
        </div>
      </template>
    </div>

    <!-- 超预期个股区域 -->
    <div class="stocks">
      <div class="stocks-title">
        <span>超预期个股</span>
        <!--
        <el-button
          type="primary"
          size="mini"
          style="margin-left: 10px;"
          @click="showDragonPool = true"
        >
          龙池
        </el-button>
        -->
      </div>
      <div class="stocks-list">
        <div 
          v-for="(stock, index) in stockList" 
          :key="index"
          class="stock-item"
          @click="handleStockClick(stock)"
        >
          <el-tag
            class="stock-tag"
            :type="stock.deltaBidAmountAtAuction > 0 ? 'danger' : 'success'"
            :effect="selectedStockCode === stock.code ? 'dark' : 'plain'"
          >
            {{ formatStockTag(stock) }}
            <span :class="getDeltaClass(stock.deltaBidAmountAtAuction)">
              ({{ formatSignedYi(stock.deltaBidAmountAtAuction) }})
            </span>
          </el-tag>
        </div>
      </div>
    </div>

    <!-- 概念展示区域 -->
    <div class="concepts-area">
      <div class="concepts-title">
        <span>概念列表（{{ conceptsList.length }}）</span>
      </div>
      <div class="concepts-list">
        <div 
          v-for="(concept, index) in conceptsList" 
          :key="index"
          class="concept-item"
        >
          <el-badge 
            :value="concept.num" 
            :max="99"
            type="primary"
          >
            <el-tag class="concept-tag">
              {{ concept.name }}
            </el-tag>
          </el-badge>
        </div>
      </div>
    </div>

    <!-- 结果表格 -->
    <div class="results-area">
      <el-table
        ref="biddingTable"
        :data="tableList"
        style="width: 100%"
        border
        :size="isMini"
        @sort-change="handleSortChange"
      >
        <el-table-column prop="name" label="股票名称" :width="!isComputer ? '80px' : '120px'">
          <template slot-scope="{ row }">
            <span v-html="row.nameDisplay || row.name"></span>
          </template>
        </el-table-column>
        <el-table-column prop="marketValue" sortable="custom" label="流通市值" :width="!isComputer ? '100px' : '120px'">
          <template slot-scope="{ row }">
            <span>{{ row.marketValue }}亿</span>
          </template>
        </el-table-column>
        <el-table-column prop="yesterdayTier" label="昨日梯队" :width="!isComputer ? '90px' : '110px'">
          <template slot-scope="{ row }">
            <span>{{ row.yesterdayTier || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="turnoverRate" sortable="custom" label="竞价实际换手" :width="!isComputer ? '120px' : '140px'"/>
        <el-table-column prop="gainPercent" sortable="custom" label="竞价涨幅" :width="!isComputer ? '100px' : '120px'">
          <template slot-scope="{ row }">
            <span :class="row.gainPercent > 0 ? 'gain-up' : 'gain-down'">
              {{ row.gainPercent > 0 ? '+' : '' }}{{ row.gainPercent }}%
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="turnoverGainProduct" sortable="custom" label="换手X涨幅" :width="!isComputer ? '110px' : '130px'"/>
        <el-table-column prop="volumeRatio" sortable="custom" label="竞价量比" :width="!isComputer ? '100px' : '120px'"/>
        <el-table-column prop="score" sortable="custom" label="实际得分" :width="!isComputer ? '100px' : '120px'"/>
        <el-table-column
          prop="concepts"
          label="概念详情"
          :min-width="biddingConceptColumnMinWidth"
          class-name="bidding-concepts-col"
        >
          <template slot-scope="{ row }">
            <div
              class="bidding-concept-tags"
              :class="{ 'bidding-concept-tags--h5-strip': isMobile }"
            >
              <el-tag
                v-for="(concept, idx) in row.concepts"
                :key="idx"
                size="mini"
              >
                {{ concept }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 龙池弹窗 -->
    <DragonPoolDialog 
      :visible.sync="showDragonPool" 
      @refresh="getBiddingData"
    />

  </div>
</template>

<script>
import { stockApi } from '@/api/stockApi';
import { BREAKPOINTS } from '@/utils/device';
import { formatDate } from '@/utils/index';
import { CONCEPT_SOURCE_OPTIONS, CONCEPT_SOURCE_MERGE } from '@/utils/conceptSource';
import DragonPoolDialog from './components/DragonPoolDialog.vue';

export default {
  name: 'BiddingAnalysis',
  components: {
    DragonPoolDialog
  },
  data() {
    return {
      isMobile: false,
      isTablet: false,
      currentDate: formatDate(),
      stockList: [],
      conceptsList: [],
      tableList: [],
      selectedStockCode: null,
      sortProp: '',
      sortOrder: '',
      showDragonPool: false,
      syncingDayData: false,
      stockListLoading: false,
      conceptSource: CONCEPT_SOURCE_MERGE,
      conceptSourceOptions: CONCEPT_SOURCE_OPTIONS
    };
  },
  computed: {
    isComputer() {
      return !this.isMobile && !this.isTablet;
    },
    /** Web 与首板预选一致：默认尺寸；H5 为 mini */
    isMini() {
      return this.isMobile ? 'mini' : '';
    },
    /** H5 与补涨分析一致：概念列宽 + 单元格内横滑 */
    biddingConceptColumnMinWidth() {
      if (this.isMobile) return '400px';
      return '200px';
    }
  },
  methods: {
    checkScreenSize() {
      const width = window.innerWidth;
      this.isMobile = width <= BREAKPOINTS.MOBILE;
      this.isTablet = width > BREAKPOINTS.MOBILE && width <= BREAKPOINTS.TABLET;
    },

    // 日期变化处理
    handleDateChange() {
      this.getBiddingData();
      this.tableList = [];
      this.selectedStockCode = null;
    },
    onConceptSourceChange() {
      this.tableList = [];
      this.selectedStockCode = null;
      this.getBiddingData();
    },
    async handleSyncDayData() {
      if (!this.currentDate) {
        this.$message.warning('请先选择日期');
        return;
      }
      this.syncingDayData = true;
      try {
        const result = await stockApi.syncBiddingDayData(this.currentDate);
        if (!result?.ok) {
          throw new Error(result?.message || '获取数据失败');
        }
        const auctionCount = Number(result?.count || 0);
        this.$message.success(`获取完成：竞价 ${auctionCount} 条`);
        await this.getBiddingData();
      } catch (error) {
        console.error('按日期获取数据失败:', error);
        this.$message.error('按日期获取数据失败');
      } finally {
        this.syncingDayData = false;
      }
    },

    // 获取竞价分析数据
    async getBiddingData() {
      try {
        const result = await stockApi.getBiddingSummary(this.currentDate, this.conceptSource);
        if (!result?.ok) {
          throw new Error(result?.message || '获取竞价数据失败');
        }
        this.stockList = Array.isArray(result.stockList) ? result.stockList : [];
        this.conceptsList = Array.isArray(result.conceptsList) ? result.conceptsList : [];
      } catch (error) {
        console.error('获取竞价数据失败:', error);
        this.stockList = [];
        this.conceptsList = [];
        this.$message.error('获取竞价数据失败');
      }
    },

    // 点击个股查询列表
    async handleStockClick(stock) {
      this.selectedStockCode = stock.code;
      this.tableList = [];
      this.stockListLoading = true;
      try {
        const result = await stockApi.getBiddingStockList(stock.code, this.currentDate, this.conceptSource);
        if (!result?.ok) {
          throw new Error(result?.message || '查询失败');
        }
        this.tableList = (Array.isArray(result.data) ? result.data : []).map((row) => {
          const matched = Array.isArray(row?.matchedNames) ? row.matchedNames : [];
          const turnoverRate = Number(row?.turnoverRate);
          const gainPercent = Number(row?.gainPercent);
          const turnoverGainProduct =
            Number.isFinite(turnoverRate) && Number.isFinite(gainPercent)
              ? Number((turnoverRate * gainPercent).toFixed(2))
              : null;
          return {
            ...row,
            nameDisplay: this.highlightMatchedText(row?.name, matched, 'highlight-name'),
            turnoverGainProduct
          };
        });
        // 默认按 换手X涨幅 降序
        this.sortProp = 'turnoverGainProduct';
        this.sortOrder = 'descending';
        this.tableList = [...this.tableList].sort((a, b) => {
          const aVal = a?.turnoverGainProduct;
          const bVal = b?.turnoverGainProduct;
          if (aVal == null) return 1;
          if (bVal == null) return -1;
          return Number(bVal) - Number(aVal);
        });
      } catch (error) {
        console.error('查询个股列表失败:', error);
        this.$message.error('查询失败');
        this.tableList = [];
      } finally {
        this.stockListLoading = false;
      }
    },

    // 排序变化处理
    handleSortChange({ prop, order }) {
      if (!order) {
        // 取消排序，恢复原始顺序
        this.sortProp = '';
        this.sortOrder = '';
        return;
      }
      
      this.sortProp = prop;
      this.sortOrder = order;
      
      // 执行排序
      this.tableList = [...this.tableList].sort((a, b) => {
        let aVal = a[prop];
        let bVal = b[prop];
        
        // 处理可能为 null/undefined 的情况
        if (aVal == null) return order === 'ascending' ? -1 : 1;
        if (bVal == null) return order === 'ascending' ? 1 : -1;
        
        // 数值比较
        const comparison = Number(aVal) - Number(bVal);
        return order === 'ascending' ? comparison : -comparison;
      });
    },
    toYi(value) {
      const n = Number(value);
      if (Number.isNaN(n)) return '--';
      return (n / 100000000).toFixed(2);
    },
    formatSignedYi(value) {
      const n = Number(value);
      if (Number.isNaN(n)) return '--';
      const yi = (Math.abs(n) / 100000000).toFixed(2);
      return `${n > 0 ? '+' : n < 0 ? '-' : ''}${yi}`;
    },
    formatStockTag(stock) {
      const boards = Number(stock?.consecutiveBoards);
      const safeBoards = Number.isFinite(boards) && boards > 0 ? boards : 1;
      return `${safeBoards}-${stock?.name || ''}${this.toYi(stock?.bidAmountAtAuction)}`;
    },
    // 高亮显示匹配的文本（同名命中：标黄）
    highlightMatchedText(text, matchedChars, className) {
      if (!text || !matchedChars || matchedChars.length === 0) return text;
      let result = String(text);
      matchedChars.forEach((char) => {
        const safe = String(char).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${safe})`, 'g');
        result = result.replace(regex, `<span class="${className}">$1</span>`);
      });
      return result;
    },
    getDeltaClass(value) {
      const n = Number(value);
      if (Number.isNaN(n) || n === 0) return 'delta-flat';
      return n > 0 ? 'delta-up' : 'delta-down';
    }
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
    this.getBiddingData();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  }
};
</script>

<style lang="scss" scoped>
.bidding {
  height: 100%;
  display: flex;
  flex-direction: column;

  .date-area {
    background: #fff;
    padding: 20px;
    border-radius: 4px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0;

    .date-label {
      margin-right: 10px;
      color: #606266;
      font-size: 14px;
      flex-shrink: 0;
    }

    .date-area__segment .date-label {
      margin-right: 0;
    }

    .date-area__segment {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      gap: 8px;
    }

    /* 仅控制「日期组」与「概念组」之间的间距（不再与 flex 全局 gap 叠加） */
    .date-area__segment--after-date {
      margin-left: 6px;
    }

    .date-area__sync-btn {
      margin-left: 10px;
    }

    /* 与首板预选、补涨分析 .search-area-select 一致 */
    ::v-deep .search-area-select.el-date-editor,
    ::v-deep .el-select.search-area-select {
      width: 220px;
    }

    &.date-area--mobile {
      flex-direction: column;
      align-items: stretch;
      /* 与首板/补涨 el-col.sm-mt 行间 15px 一致 */
      gap: 15px;

      .date-area__row {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 8px;
        min-width: 0;
      }

      /* 勿 wrap：窄屏下 select+按钮 折行会撑出 ~64px 空白感 */
      .date-area__row--concept {
        flex-wrap: nowrap;
        gap: 6px;

        ::v-deep .el-select.search-area-select {
          flex: 1 1 auto;
          min-width: 0;
          max-width: 200px;
        }

        .date-area__sync-btn {
          flex-shrink: 0;
        }
      }

      .date-area__sync-btn {
        margin-left: 0;
      }
    }
  }

  /* 与 FirstBoardPreselection / GapUpAnalysis @media (max-width: 1023px) 一致 */
  @media screen and (max-width: 1023px) {
    .date-area {
      padding: 10px;
      margin-bottom: 10px;

      ::v-deep .search-area-select.el-date-editor,
      ::v-deep .el-select.search-area-select {
        width: calc(100% - 60px);
        max-width: 200px;
      }
    }

    .stocks {
      padding: 10px;
      margin-bottom: 10px;
    }

    .concepts-area {
      padding: 10px;
      margin-bottom: 10px;
    }

    .results-area {
      padding: 10px;
    }
  }

  .stocks {
    background: #fff;
    padding: 10px 20px;
    border-radius: 4px;
    margin-bottom: 20px;

    .stocks-title {
      font-weight: bold;
      margin: 10px 0 15px 0;
      display: flex;
      align-items: center;
      font-size: 14px;
      color: #303133;
    }

    .stocks-list {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      padding-top: 5px;

      .stock-item {
        cursor: pointer;
      }

      .stock-tag {
        cursor: pointer;
        transition: all 0.3s;

        .delta-up {
          color: #f56c6c;
          font-weight: 600;
        }

        .delta-down {
          color: #67c23a;
          font-weight: 600;
        }

        .delta-flat {
          color: #909399;
          font-weight: 600;
        }

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        }
      }
    }
  }

  .concepts-area {
    background: #fff;
    padding: 10px 20px;
    border-radius: 4px;
    margin-bottom: 20px;

    .concepts-title {
      font-weight: bold;
      margin: 10px 0 15px 0;
      display: flex;
      align-items: center;
      font-size: 14px;
      color: #303133;
    }

    .concepts-list {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;
      padding-top: 5px;
      min-height: 48px;

      .concept-tag {
        cursor: pointer;
        transition: all 0.3s;
      }

      .concept-tag:hover {
        transform: translateY(-2px);
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      }
    }
  }

  .results-area {
    background: #fff;
    padding: 10px 20px 20px 20px;
    border-radius: 4px;
    flex-grow: 1;
    height: 0;
    display: flex;
    flex-direction: column;

    ::v-deep .el-table {
      flex-grow: 1;

      .el-table__body-wrapper {
        height: calc(100% - 41px);
        overflow-y: auto;
      }

      /* Web：概念可多行换行；H5 见下方 @media */
      @media screen and (min-width: 769px) {
        td.bidding-concepts-col .cell {
          white-space: normal !important;
          word-break: break-word;
        }
      }

      .highlight-name {
        background: #fff2a8;
        padding: 2px;
        border-radius: 2px;
      }
    }

    .bidding-concept-tags {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px;
    }

    .gain-up {
      color: #f56c6c;
      font-weight: 500;
    }

    .gain-down {
      color: #67c23a;
      font-weight: 500;
    }

  }

  // 移动端适配
  @media screen and (max-width: 768px) {
    .date-area {
      padding: 10px;
      margin-bottom: 10px;

      .date-label {
        font-size: 13px;
      }

      /* 覆盖 App.vue 全局 .el-date-editor / .el-select 在 H5 上的 width:100% */
      ::v-deep .search-area-select.el-date-editor,
      ::v-deep .el-select.search-area-select {
        width: calc(100% - 60px) !important;
        max-width: 200px !important;
      }

      /* 概念行：select 与按钮同一行，勿用「接近整行」宽度否则按钮换行、行高 ~64px */
      &.date-area--mobile .date-area__row--concept {
        ::v-deep .el-select.search-area-select {
          width: auto !important;
          max-width: 200px !important;
          flex: 1 1 auto;
          min-width: 72px;
        }
      }
    }

    .stocks {
      padding: 10px;
      margin-bottom: 10px;

      .stocks-title {
        margin: 5px 0 10px 0;
        font-size: 13px;
      }

      .stocks-list {
        gap: 8px;
      }
    }

    .concepts-area {
      padding: 10px;
      margin-bottom: 10px;
      flex-shrink: 0;

      .concepts-title {
        margin: 5px 0 10px 0;
        font-size: 13px;
      }

      .concepts-list {
        gap: 8px;
        max-height: 160px;
        overflow-x: hidden;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }
    }

    .results-area {
      padding: 10px;

      ::v-deep td.bidding-concepts-col .cell {
        overflow: hidden !important;
        vertical-align: middle;
        white-space: normal !important;
      }

      .bidding-concept-tags--h5-strip {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 4px;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }

        ::v-deep .el-tag {
          flex-shrink: 0;
        }
      }
    }
  }
}
</style>
